import React from "react";
import FlowPanel from "./FlowPanel";

const NodeEditorCanvas = React.memo(({
  flowAreaRef,
  panOffset,
  zoom,
  safeGridWidth,
  safeGridHeight,
  minX,
  minY,
  gridDots,
  connections,
  flowItems,
  flowSteps,
  animatingPanels,
  dragOverPosition,
  draggedPanel,
  hoveredStackingItem,
  stackingOrder,
  expandedComponents,
  dragOverStep,
  isPanning,
  isScrollingRef,
  isScrolling,
  timelineHeight,
  currentAction,
  ACTION_STATE,
  hoveredTimelineComponentId,
  onMouseDown,
  onFlowAreaDragOver,
  onFlowAreaDragLeave,
  onInsertionDrop,
  onPanelDragStart,
  onPanelDragEnd,
  onStepNameChange,
  onComponentNameChange,
  isEditingName,
  setIsEditingName,
  onTimelineResizeStart,
  onComponentDragStart,
  onComponentDragEnd,
  onStepContextMenu,
  onComponentContextMenu,
  onComponentClick,
  onEditComponentCanvas,
  onComponentHover,
  onBackgroundClick,
  leftListNewCustomisationItems,
  campaignData,
  customisationVars,
  selectedFeatureId,
  selectedFeatureNode,
  selectedItem,
  onComponentTimingChange,
  activeFlowLoopEnabled,
  activeFlowLoopDelay,
  onFlowLoopSettingsChange,
  showFlowLoopControl = true,
  getFlowStepListNodesForPreviewFunc,
  activateStepFunc,
  previewRefreshToken,
}) => {
  const flowLoopControlRef = React.useRef(null);

  const normalizeId = (value) => (value === undefined || value === null ? null : String(value));

  React.useEffect(() => {
    if (!activeFlowLoopEnabled) {
      return undefined;
    }

    const handleDocumentMouseDown = (event) => {
      if (!flowLoopControlRef.current) {
        return;
      }

      if (flowLoopControlRef.current.contains(event.target)) {
        return;
      }

      onFlowLoopSettingsChange?.({ loop: false });
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);

    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
    };
  }, [activeFlowLoopEnabled, onFlowLoopSettingsChange]);

  const resolveStepIndexForPanel = (panelItem) => {
    const panelStepId = normalizeId(panelItem?.stepId);
    const panelId = normalizeId(panelItem?.id);

    const directMatchIndex = flowSteps.findIndex((step) => {
      const stepId = normalizeId(step?.id);
      const stepStepId = normalizeId(step?.stepId);

      return (
        (panelStepId && (stepStepId === panelStepId || stepId === panelStepId))
        || (panelId && (stepStepId === panelId || stepId === panelId))
      );
    });

    if (directMatchIndex >= 0) {
      return directMatchIndex;
    }

    if (!Array.isArray(flowSteps) || flowSteps.length === 0) {
      return -1;
    }

    // Fallback to nearest X match when IDs are temporarily out of sync.
    const panelX = Number(panelItem?.x) || 0;
    let nearestIndex = -1;
    let nearestDistance = Number.POSITIVE_INFINITY;

    flowSteps.forEach((step, index) => {
      const stepX = Number(step?.x) || 0;
      const distance = Math.abs(stepX - panelX);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    return nearestIndex;
  };

  const endOfFlowItem = flowItems.find((item) => item.id === 'plus-end');
  const lastPanel = flowItems
    .filter((item) => item.type === 'panel')
    .reduce((currentLastPanel, item) => {
      if (!currentLastPanel) {
        return item;
      }

      const currentRightEdge = currentLastPanel.x + (currentLastPanel.width || 0);
      const itemRightEdge = item.x + (item.width || 0);
      return itemRightEdge > currentRightEdge ? item : currentLastPanel;
    }, null);

  const connectorLineY = endOfFlowItem ? (endOfFlowItem.y - minY + 15) : 0;
  const lastPanelRightEdge = lastPanel ? (lastPanel.x - minX + (lastPanel.width || 0)) : null;
  const loopButtonSize = 28;
  const loopButtonGap = 112;
  const loopButtonLeft = lastPanelRightEdge !== null ? lastPanelRightEdge + loopButtonGap : (endOfFlowItem ? endOfFlowItem.x - minX + 70 : 0);
  const loopButtonCenterX = loopButtonLeft + (loopButtonSize / 2);
  const connectorLeft = lastPanelRightEdge !== null ? lastPanelRightEdge : (endOfFlowItem ? endOfFlowItem.x - minX + 14 : 0);
  const connectorWidth = Math.max(0, loopButtonCenterX - connectorLeft);
  const normalizedLoopDelaySeconds = Math.max(0, Number(activeFlowLoopDelay) || 0);
  const loopDelayMinutes = Math.floor(normalizedLoopDelaySeconds / 60);
  const loopDelaySeconds = normalizedLoopDelaySeconds % 60;

  const updateLoopDelayMinutes = (minutesValue) => {
    const nextMinutes = Math.max(0, Number(minutesValue) || 0);
    onFlowLoopSettingsChange?.({ loop: true, loopDelay: (nextMinutes * 60) + loopDelaySeconds });
  };

  const updateLoopDelaySeconds = (secondsValue) => {
    const nextSeconds = Math.max(0, Math.min(59, Number(secondsValue) || 0));
    onFlowLoopSettingsChange?.({ loop: true, loopDelay: (loopDelayMinutes * 60) + nextSeconds });
  };

  return (
    <>
      <div 
        className={`flow-area ${isPanning ? 'panning' : ''} ${currentAction === ACTION_STATE?.PANNING ? 'panning-active' : ''}`}
        ref={flowAreaRef}
        style={{ flex: 1, position: 'relative' }}
        onClick={(e) => {
          if (!onBackgroundClick) return;
          const clickedOnComponent = e.target.closest?.('.component-item');
          const clickedOnPanel = e.target.closest?.('.flow-panel');
          if (!clickedOnComponent && !clickedOnPanel) onBackgroundClick();
        }}
      >
        <div 
          className="node-editor-section"
          onMouseDown={onMouseDown}
          onDragOver={onFlowAreaDragOver}
          onDragLeave={onFlowAreaDragLeave}
          onDrop={onInsertionDrop}
        >
        <div 
          className="flow-container"
          style={{
            transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom})`,
            width: safeGridWidth,
            height: safeGridHeight,
          }}
        >
          {/* <div 
            className="grid-bg"
            style={{
              width: safeGridWidth,
              height: safeGridHeight,
            }}
          >
            {/* Render grid dots */}
            {/*gridDots.map((dot) => (
              <div
                key={dot.key}
                className="grid-dot"
                style={{
                  left: dot.x,
                  top: dot.y,
                }}
              />
            ))}
          </div> */}
          
          {/* Render connection lines */}
          <svg
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: safeGridWidth,
              height: safeGridHeight,
              pointerEvents: 'none',
              zIndex: 2,
            }}
            viewBox={`0 0 ${safeGridWidth} ${safeGridHeight}`}
          >
            {connections.map((connection) => (
              <g key={connection.key}>
                {/* Only render line if it's not just a dot */}
                {!connection.isJustDot && (
                  <line
                    x1={connection.x1}
                    y1={connection.y1}
                    x2={connection.x2}
                    y2={connection.y2}
                    stroke={connection.isExpansionLine ? connection.strokeColor : "#007bff"}
                    strokeWidth={connection.isExpansionLine ? "1" : "2"}
                    strokeDasharray={connection.isExpansionLine ? "4,3" : "4,4"}
                    opacity={connection.isExpansionLine ? "0.75" : "1"}
                  />
                )}
                {/* Render dots using x,y properties (not x1,y1) */}
                {connection.isJustDot && connection.x !== undefined && connection.y !== undefined && (
                  <circle
                    cx={connection.x}
                    cy={connection.y}
                    r="4"
                    fill="#007bff"
                  />
                )}
              </g>
            ))}
          </svg>
          
          {/* Render flow items above the grid */}
          {flowItems.map((item, index) => {
            // Calculate dynamic positioning for insertion animation
            let dynamicStyle = {
              left: item.x - minX,
              top: item.y - minY,
              width: item.width,
              height: item.height,
              transition: animatingPanels.includes(item.id) ? 'left 300ms ease-out' : 'none',
            };

            if (draggedPanel && item.id === draggedPanel.id) {
              dynamicStyle.opacity = 0.5;
              dynamicStyle.transform = 'scale(0.95)';
              dynamicStyle.transition = 'none';
            }

            if (item.type === 'plus') {
              return null;
            }

            // Patch: For each panel, collect all components that overlap this step (expanded or not)
            // Find the step index for this panel
            const stepIndex = resolveStepIndexForPanel(item);
            // Gather all components from all steps that span into this step
            let allOverlappingComponents = [];
            flowSteps.forEach((step, sIdx) => {
              (step.components || []).forEach(component => {
                const stepLength = component.stepLength || 1;
                const startStep = sIdx;
                const endStep = sIdx + stepLength - 1;
                if (stepIndex >= startStep && stepIndex <= endStep) {
                  // Mark as expanded copy if not home step
                  allOverlappingComponents.push({
                    ...component,
                    isExpansionLinked: stepLength > 1,
                    isExpandedCopy: stepIndex !== startStep,
                    homeStepId: step.stepId || step.id,
                  });
                }
              });
            });
            // Sort by stacking order (zIndex)
            allOverlappingComponents.sort((a, b) => {
              const aStackItem = stackingOrder?.find(s => s.featureId === (a.featureId || a.campaignFeatureId));
              const bStackItem = stackingOrder?.find(s => s.featureId === (b.featureId || b.campaignFeatureId));
              const aIndex = aStackItem?.zIndex ?? 999;
              const bIndex = bStackItem?.zIndex ?? 999;
              return aIndex - bIndex;
            });

            // Pass patched components array to FlowPanel
            return (
              <FlowPanel
                key={item.id}
                stepIndex={stepIndex}
                item={{ ...item, components: allOverlappingComponents }}
                dynamicStyle={dynamicStyle}
                hoveredStackingItem={hoveredStackingItem}
                hoveredTimelineComponentId={hoveredTimelineComponentId}
                stackingOrder={stackingOrder}
                expandedComponents={expandedComponents}
                dragOverStep={dragOverStep}
                isScrollingRef={isScrollingRef}
                isScrolling={isScrolling}
                isPanning={isPanning}
                onPanelDragStart={onPanelDragStart}
                onPanelDragEnd={onPanelDragEnd}
                onStepNameChange={onStepNameChange}
                onComponentNameChange={onComponentNameChange}
                isEditingName={isEditingName}
                setIsEditingName={setIsEditingName}
                onComponentDragStart={onComponentDragStart}
                onComponentDragEnd={onComponentDragEnd}
                onStepContextMenu={onStepContextMenu}
                onComponentContextMenu={onComponentContextMenu}
                onInsertionDrop={onInsertionDrop}
                onFlowAreaDragOver={onFlowAreaDragOver}
                onComponentClick={onComponentClick}
                onEditComponentCanvas={onEditComponentCanvas}
                onComponentHover={onComponentHover}
                selectedFeatureId={selectedFeatureId}
                selectedFeatureNode={selectedFeatureNode}
                campaignData={campaignData}
                customisationVars={customisationVars}
                leftListNewCustomisationItems={leftListNewCustomisationItems}
                selectedItem={selectedItem}
                onComponentTimingChange={onComponentTimingChange}
                panOffset={panOffset}
                getFlowStepListNodesForPreviewFunc={getFlowStepListNodesForPreviewFunc}
                activateStepFunc={activateStepFunc}
                previewRefreshToken={previewRefreshToken}
              />
            );
          })}
          
          {/* Render temporary insertion plus sign when dragging between panels */}
          {dragOverPosition && (
            <div
              className="plus-sign insertion-preview"
              style={{
                left: dragOverPosition.x - minX,
                top: dragOverPosition.y - minY,
              }}
            >
              +
            </div>
          )}

          {showFlowLoopControl && endOfFlowItem && (
            <>
              <div
                className="flow-end-loop-connector"
                style={{
                  left: connectorLeft,
                  top: connectorLineY,
                  width: connectorWidth,
                }}
              />
              <div
                className={`flow-end-loop-control${activeFlowLoopEnabled ? ' is-enabled' : ''}`}
                ref={flowLoopControlRef}
                style={{
                  left: loopButtonLeft,
                  top: connectorLineY - (loopButtonSize / 2),
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className={`flow-end-loop-toggle feature-chain-loop-toggle${activeFlowLoopEnabled ? ' is-enabled' : ''}`}
                  onClick={() => onFlowLoopSettingsChange?.({ loop: !activeFlowLoopEnabled })}
                  aria-label={activeFlowLoopEnabled ? 'Disable loop flow' : 'Enable loop flow'}
                >
                  ↻
                </button>
                {activeFlowLoopEnabled && (
                  <label className="flow-end-loop-delay-field">
                    <span>Delay</span>
                    <span className="flow-end-loop-time-label">Min</span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={loopDelayMinutes}
                      onChange={(e) => updateLoopDelayMinutes(e.target.value)}
                    />
                    <span className="flow-end-loop-time-label">Sec</span>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      step="1"
                      value={loopDelaySeconds}
                      onChange={(e) => updateLoopDelaySeconds(e.target.value)}
                    />
                  </label>
                )}
              </div>
            </>
          )}
        </div>
        </div>
      </div>
      
      {/* Resize handle */}
      {/* <div 
        className="timeline-resize-handle"
        onMouseDown={onTimelineResizeStart}
      >
        <div className="resize-handle-line"></div>
      </div> */}
    </>
  );
}, (prevProps, nextProps) => {
  // Only re-render if critical props change
  // hoveredStackingItem changes frequently but doesn't need full re-render
  return (
    prevProps.panOffset.x === nextProps.panOffset.x &&
    prevProps.panOffset.y === nextProps.panOffset.y &&
    prevProps.zoom === nextProps.zoom &&
    prevProps.isPanning === nextProps.isPanning &&
    prevProps.isEditingName === nextProps.isEditingName &&
    prevProps.draggedPanel?.id === nextProps.draggedPanel?.id &&
    prevProps.dragOverPosition === nextProps.dragOverPosition &&
    prevProps.hoveredStackingItem === nextProps.hoveredStackingItem &&
    prevProps.hoveredTimelineComponentId === nextProps.hoveredTimelineComponentId &&
    prevProps.selectedFeatureId === nextProps.selectedFeatureId &&
    prevProps.activeFlowLoopEnabled === nextProps.activeFlowLoopEnabled &&
    prevProps.activeFlowLoopDelay === nextProps.activeFlowLoopDelay &&
    prevProps.showFlowLoopControl === nextProps.showFlowLoopControl &&
    prevProps.onBackgroundClick === nextProps.onBackgroundClick &&
    prevProps.onEditComponentCanvas === nextProps.onEditComponentCanvas &&
    prevProps.flowItems.length === nextProps.flowItems.length &&
    JSON.stringify(prevProps.connections.map(conn => ({
      key: conn.key,
      x1: conn.x1,
      y1: conn.y1,
      x2: conn.x2,
      y2: conn.y2,
      x: conn.x,
      y: conn.y,
      isExpansionLine: conn.isExpansionLine,
      strokeColor: conn.strokeColor,
      isJustDot: conn.isJustDot
    }))) === JSON.stringify(nextProps.connections.map(conn => ({
      key: conn.key,
      x1: conn.x1,
      y1: conn.y1,
      x2: conn.x2,
      y2: conn.y2,
      x: conn.x,
      y: conn.y,
      isExpansionLine: conn.isExpansionLine,
      strokeColor: conn.strokeColor,
      isJustDot: conn.isJustDot
    }))) &&
    JSON.stringify(prevProps.flowItems.map(fi => ({ 
      id: fi.id,
      x: fi.x,
      y: fi.y,
      label: fi.label,
      components: (fi.components || []).map(component => ({
        id: component.id,
        label: component.label,
        isPrimaryStepItem: component.isPrimaryStepItem,
      }))
    }))) === 
    JSON.stringify(nextProps.flowItems.map(fi => ({ 
      id: fi.id,
      x: fi.x,
      y: fi.y,
      label: fi.label,
      components: (fi.components || []).map(component => ({
        id: component.id,
        label: component.label,
        isPrimaryStepItem: component.isPrimaryStepItem,
      }))
    })))
  );
});

export default NodeEditorCanvas;
